/**
 * Shared helpers that derive the active-filter view from `DiscoverParams`.
 *
 * Two surfaces consume these:
 *
 *   1. `DiscoverActiveFilters` (the chip row in the rail + drawer) — uses
 *      `deriveActiveFilters` to render one chip per active facet.
 *   2. `MobileFilterDrawer` (the `Filters · N` trigger button) — uses
 *      `countActiveFilters` to label the trigger with the count.
 *
 * Keeping the count and the chip list in one module guarantees that the
 * "Filters · N" label can never drift away from the visible chip count. Both
 * derive from the same `MULTI_KEYS` and single-field allowlist, so adding a
 * new facet only requires touching this file plus the rendering surfaces.
 *
 * @since WEB-273 (Discover · Tri-View Template — M3, mobile filter drawer)
 */

import type { DiscoverParams } from './url-state';

/**
 * Multi-select facets that contribute one count + one chip per array entry.
 *
 * WEB-741: `venue` (FRE-1145) joins the list. It lost its only UI producer when
 * WEB-662 retired `DiscoverFollowedVenuesRail`, whose checkbox rows were both
 * the indicator AND the remover — leaving `?venue=` an active filter that
 * narrowed results with no chip, no count, and no way to clear it short of the
 * empty-state "Clear all" (which only appears when the list is empty and wipes
 * every other filter). The key itself is still honoured end to end, so the fix
 * is to make it visible rather than to delete it and silently widen existing
 * saved searches. Living in the chip strip rather than a rail section means the
 * venue v2 redesign can supersede this without touching rail composition.
 */
const MULTI_KEYS = ['category', 'neighborhood', 'venue'] as const;

/**
 * Single-value facets that each contribute exactly one count + one chip when
 * set. `q` is included because the search query is rendered as a chip too —
 * see `DiscoverActiveFilters.formatSingleLabel`.
 *
 * WEB-662 (DPL-12): `followedOnly` is included for the same reason every other
 * rail filter is. It is the only filter reachable from the mobile sheet whose
 * control sits BELOW the fold, so without a chip + count a viewer who lands on
 * a shared `?followedOnly=true` link sees a narrowed list, a trigger reading a
 * bare "Filters", an empty chip strip, and no way to clear it short of
 * reopening the sheet and scrolling. It is a boolean rather than a string, but
 * the truthiness test below and `setParams({ [key]: undefined })` in the chip's
 * remove handler both work unchanged on it.
 */
const SINGLE_KEYS = ['date', 'price', 'q', 'followedOnly'] as const;

type MultiKey = (typeof MULTI_KEYS)[number];
type SingleKey = (typeof SINGLE_KEYS)[number];

export type DiscoverActiveFilterMultiKey = MultiKey;
export type DiscoverActiveFilterSingleKey = SingleKey;

export interface DiscoverActiveFilterDescriptor {
  /** Stable React key — `"<facet>"` for single, `"<facet>:<value>"` for multi. */
  readonly key: string;
  /** Discriminated identifier consumed by chip-remove handlers. */
  readonly removeKey:
    | { readonly kind: 'multi'; readonly key: MultiKey; readonly value: string }
    | { readonly kind: 'single'; readonly key: SingleKey };
  /** Raw facet kind for label formatting at the call site. */
  readonly facet: MultiKey | SingleKey;
  /** Raw value for multi-select chips. `undefined` for single-value facets. */
  readonly value?: string;
}

/**
 * Count the number of distinct active filters expressed in the URL state.
 *
 * The tally always equals the number of chips `DiscoverActiveFilters` renders
 * — one chip per multi-select value, plus one each for `date`, `price`, `q`.
 *
 * Pure function: stable across renders for the same `params` reference.
 */
export function countActiveFilters(params: DiscoverParams): number {
  let count = 0;
  for (const key of MULTI_KEYS) {
    count += params[key].length;
  }
  for (const key of SINGLE_KEYS) {
    if (params[key]) count += 1;
  }
  return count;
}

/**
 * Derive the active-filter descriptor array for chip rendering. The order
 * matches the chip strip the rail renders today — multi facets first (in the
 * order declared by `MULTI_KEYS`), then `date`, `price`, `q`.
 */
export function deriveActiveFilterDescriptors(
  params: DiscoverParams,
): DiscoverActiveFilterDescriptor[] {
  const out: DiscoverActiveFilterDescriptor[] = [];

  for (const key of MULTI_KEYS) {
    for (const value of params[key]) {
      out.push({
        key: `${key}:${value}`,
        removeKey: { kind: 'multi', key, value },
        facet: key,
        value,
      });
    }
  }

  for (const key of SINGLE_KEYS) {
    const value = params[key];
    if (!value) continue;
    out.push({
      key,
      removeKey: { kind: 'single', key },
      facet: key,
    });
  }

  return out;
}
